
import useNavigation from './useNavigation';
import MissionService from '../../../../service/MissionService';
import { getFormattedFaultsPlanData } from '../utils/helperFunction';
import { FaultReportType, FaultsOptionType, FaultSubsystemsType } from '../utils/scenario-types';
import { getApiFailureMessage } from '../../../../utils/commonFunctions';
import { useContext } from 'react';
import { ScenarioStatesProviderContext } from '../providers/StatesProvider';

type FaultResponseType = {
    status: "completed" | "idle"
    data: FaultReportType
}

const useFaultApis = () => {
    const { missionId, scenarioId } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useFaultApis must be used within a Scenario States Provider')
    }

    const { setFormData } = context
    const getallFaultReportData = (): Promise<FaultResponseType> => {
        return new Promise(async (resolve) => {
            try {
                const response = await MissionService.getAllFaultScheduleData(missionId, scenarioId);
                const faults = response?.data?.data;
                const faultPlan: FaultResponseType = {
                    status: faults?.length > 0 ? "completed" : "idle",
                    data: faults?.length > 0 ? getFormattedFaultsPlanData(response?.data?.data) : null
                };
                resolve(faultPlan);
            } catch (error: any) {
                resolve({
                    status: "idle",
                    data: null
                });
            }
        });
    };

    const postFaultData = (faultscheduleId: string, body: any): Promise<{
        status: string;
        message: string;
    }> => {
        return new Promise(async (resolve) => {
            try {
                const response = await MissionService.patchFaultScheduleData(missionId, scenarioId, faultscheduleId, body);
                const faultPlans = await getallFaultReportData();
                setFormData((prev => ({
                    ...prev,
                    assets: {
                        ...prev?.assets,
                        faultPlans: faultPlans
                    }
                })))
                resolve({
                    status: "success",
                    message: response?.data?.message || "Fault added successfully",
                });
            } catch (error) {
                const message = getApiFailureMessage(error, "Post");
                resolve({
                    status: "error",
                    message: message,
                }); // resolve even on error
            }
        });
    };


    const deleteFaultData = (faultscheduleId: string, faultId: string): Promise<{
        status: string;
        message: string;
    }> => {
        return new Promise(async (resolve) => {
            try {
                const response = await MissionService.deleteFaultsfromschedule(missionId, scenarioId, faultscheduleId, faultId);
                const faultPlans = await getallFaultReportData();
                setFormData((prev => ({
                    ...prev,
                    assets: {
                        ...prev?.assets,
                        faultPlans: faultPlans
                    }
                })))
                resolve({
                    status: "success",
                    message: response?.data?.message || "Fault deleted successfully",
                });
            } catch (error) {
                const message = getApiFailureMessage(error, "Delete");
                resolve({
                    status: "error",
                    message: message,
                }); // resolve even on error
            }
        });
    };


    const getFaultsByGroupId = (missionId: string, groupId: string): Promise<FaultsOptionType[]> => {
        return new Promise((resolve, reject) => {
            MissionService.getAllFaultPlansBySatId(missionId, groupId)
                .then((response) => {
                    if (response?.data && response?.data?.data?.length > 0) {
                        resolve(response?.data?.data);
                    } else {
                        resolve([]);
                    }
                })
                .catch((error) => {
                    reject(new Error('Failed to fetch fault details: ' + error));
                });
        });
    }

    const getFaultSubsystemByGroupId = (missionId: string, groupId: string): Promise<FaultSubsystemsType> => {
        return new Promise((resolve, reject) => {
            MissionService.getFaultDetails(missionId, groupId)
                .then((response) => {
                    if (response?.data) {
                        resolve(response.data.data);
                    } else {
                        resolve({});
                    }
                })
                .catch((error) => {
                    reject(new Error('Failed to fetch fault subsystems: ' + error));
                });
        });
    }

    return {
        postFaultData,
        deleteFaultData,
        getFaultsByGroupId,
        getallFaultReportData,
        getFaultSubsystemByGroupId,
    }
}

export default useFaultApis